import kebabCase from 'lodash/kebabCase';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { useField, useForm } from 'vee-validate';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useLocale } from '@silae/composables';
import { ReportType } from '~/services';
import { useAuthStore } from '~/stores';
import { generateYearMonthItems } from '~/utils';

export type BillingActivityReportForm = {
	startPeriod: string;
	endPeriod: string;
	types: ReportType[];
};

export function useBillingActivityReportForm() {
	const { t } = useI18n();
	const { locale } = useLocale();

	const authStore = useAuthStore();
	const { userPartnerCreationDate } = storeToRefs(authStore);
	const { handleSubmit, resetForm } = useForm<BillingActivityReportForm>();
	const startPeriodField = useField<string>('startPeriod', `required`);
	const endPeriodField = useField<string>('endPeriod', `required`);
	const typesField = useField<ReportType[]>('types', `required`);
	const typesItems = Object.values(ReportType).map((reportType: ReportType) => ({
		label: t(`billing.report-type-enum.${kebabCase(reportType)}`),
		value: reportType
	}));

	const startPeriodItems = computed(() =>
		generateYearMonthItems(userPartnerCreationDate.value, locale.value, (year: number, month: number) => {
			if (!endPeriodField.value.value) {
				return false;
			}

			const endPeriodDate = DateTime.fromFormat(endPeriodField.value.value, 'yyyy-MM');
			const endPeriodMonth = endPeriodDate.month;
			const endPeriodYear = endPeriodDate.year;
			return (endPeriodMonth < month && endPeriodYear === year) || endPeriodYear < year;
		})
	);

	const endPeriodItems = computed(() =>
		generateYearMonthItems(userPartnerCreationDate.value, locale.value, (year: number, month: number) => {
			if (!startPeriodField.value.value) {
				return false;
			}

			const startPeriodDate = DateTime.fromFormat(startPeriodField.value.value, 'yyyy-MM');
			const startPeriodMonth = startPeriodDate.month;
			const startPeriodYear = startPeriodDate.year;
			return (startPeriodMonth > month && startPeriodYear === year) || startPeriodYear > year;
		})
	);

	return {
		/** Refs and computed */
		startPeriodField,
		startPeriodItems,
		endPeriodField,
		endPeriodItems,
		typesField,
		typesItems,

		/** Functions */
		handleSubmit,
		resetForm
	};
}
